import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';
import { Skeleton } from '@/components/ui/skeleton';
import { useGeoDefaults } from '@/hooks/useGeoDefaults';
import { useAuth } from '@/hooks/use-auth-compat';
import { currencySymbol as getCurrencySymbol } from '@/lib/currency';
import { useTeachingModes } from '@/hooks/useValueCatalog';

const TIMELINE_OPTIONS = [
  { value: 'THIS_WEEK', label: 'This week' },
  { value: 'THIS_MONTH', label: 'This month' },
  { value: 'LATER', label: 'Later' },
];

interface Step3Data {
  budgetMin: number | undefined;
  budgetMax: number | undefined;
  currency: string;
  modePreference: string;
  startTimeline: string;
}

interface Step3Props {
  pendingTeacherName?: string;
  onFinish: (data: Step3Data) => void;
  onBack: () => void;
  isSubmitting: boolean;
}

export function Step3Preferences({ pendingTeacherName, onFinish, onBack, isSubmitting }: Step3Props) {
  // All hooks must be declared before any early returns (React rules of hooks)
  const { user } = useAuth();
  const accessToken = user?.access_token;
  // The student is already authenticated at this point in the onboarding flow
  // (this step runs post-registration) — passing the token lets their persisted
  // profile currency (if already set) override their physical location. See ADR 067.
  const { data: geoDefaults, isLoading: isGeoLoading } = useGeoDefaults(accessToken);
  const teachingModes = useTeachingModes();
  const [modePreference, setModePreference] = useState('');
  const [startTimeline, setStartTimeline] = useState('');
  // User-edited slider values — undefined until the user interacts with the slider.
  // Effective value falls back to geo-defaults when undefined, so no useEffect reset needed.
  const [sliderMin, setSliderMin] = useState<number | undefined>(undefined);
  const [sliderMax, setSliderMax] = useState<number | undefined>(undefined);

  // Derive geo-aware budget values (USD/$20-$200 fallback matches GeoDefaultsService.DEFAULT)
  // SCRUM-365: backend currency only — no literal currency fallback (missing = no symbol).
  const currency = geoDefaults?.currency ?? '';
  const budgetMin = geoDefaults?.minPrice ?? 20;
  const budgetMax = geoDefaults?.maxPrice ?? 200;
  const budgetStep = Math.max(1, Math.round((budgetMax - budgetMin) / 40));
  const currencySymbol = getCurrencySymbol(currency);

  // Effective slider values: user-set if touched, geo-defaults otherwise.
  // This avoids a useEffect reset anti-pattern — the slider renders once with
  // geo-defaults values and the user owns it from that point on.
  const effectiveBudgetMin = sliderMin ?? budgetMin;
  const effectiveBudgetMax = sliderMax ?? budgetMax;

  const ctaLabel = pendingTeacherName
    ? `Show me ${pendingTeacherName}'s profile`
    : 'Take me to search results';

  const handleFinish = () => {
    onFinish({
      budgetMin: effectiveBudgetMin,
      budgetMax: effectiveBudgetMax,
      currency,
      modePreference,
      startTimeline,
    });
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-foreground mb-1">Practical details</h2>
        <p className="text-sm text-muted-foreground">All fields are optional — skip anything you're unsure about.</p>
      </div>

      {/* Budget — single range slider (two thumbs) */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <label className="text-sm font-medium text-foreground">Budget per session <span className="text-muted-foreground font-normal">(optional)</span></label>
          {!isGeoLoading && (
            <span className="text-sm font-medium text-primary">{currencySymbol}{effectiveBudgetMin} – {currencySymbol}{effectiveBudgetMax}</span>
          )}
        </div>
        {isGeoLoading ? (
          // Show skeleton while geo-defaults load — prevents rendering the slider with wrong
          // initial values and then resetting it via useEffect (race condition anti-pattern)
          <Skeleton className="h-10 w-full rounded-md" />
        ) : (
          <div className="pt-1">
            <Slider
              value={[effectiveBudgetMin, effectiveBudgetMax]}
              min={budgetMin}
              max={budgetMax}
              step={budgetStep}
              minStepsBetweenThumbs={1}
              onValueChange={(v) => {
                setSliderMin(v[0]);
                setSliderMax(v[1]);
              }}
              aria-label="Budget per session range"
            />
            <div className="flex items-center justify-between text-xs text-muted-foreground mt-1.5">
              <span>{currencySymbol}{budgetMin}</span>
              <span>{currencySymbol}{budgetMax}</span>
            </div>
          </div>
        )}
      </div>

      {/* Mode preference — options from the teaching-modes catalog; leaving all
          unselected means "no preference" (no mode filter applied). */}
      <div className="space-y-2">
        <label className="text-sm font-medium text-foreground">Preferred mode <span className="text-muted-foreground font-normal">(optional)</span></label>
        {teachingModes.isError ? (
          <p role="alert" className="text-sm text-destructive">Couldn't load options. Retrying…</p>
        ) : (
          <div className="flex flex-wrap gap-2">
            {teachingModes.entries.map(m => (
              <button
                key={m.code}
                type="button"
                disabled={teachingModes.isLoading}
                onClick={() => setModePreference(prev => prev === m.code ? '' : m.code)}
                className={[
                  'px-4 py-2 rounded-full text-sm font-medium border transition-colors duration-150 cursor-pointer',
                  modePreference === m.code
                    ? 'bg-primary text-primary-foreground border-primary'
                    : 'bg-background text-foreground border-border hover:border-primary/50 hover:bg-muted/50',
                ].join(' ')}
              >
                {m.label}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Start timeline */}
      <div className="space-y-2">
        <label className="text-sm font-medium text-foreground">When do you want to start? <span className="text-muted-foreground font-normal">(optional)</span></label>
        <div className="flex flex-wrap gap-2">
          {TIMELINE_OPTIONS.map(t => (
            <button
              key={t.value}
              type="button"
              onClick={() => setStartTimeline(prev => prev === t.value ? '' : t.value)}
              className={[
                'px-4 py-2 rounded-full text-sm font-medium border transition-colors duration-150 cursor-pointer',
                startTimeline === t.value
                  ? 'bg-primary text-primary-foreground border-primary'
                  : 'bg-background text-foreground border-border hover:border-primary/50 hover:bg-muted/50',
              ].join(' ')}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      <div className="flex gap-3">
        <Button variant="outline" onClick={onBack} className="flex-1 cursor-pointer" size="lg" disabled={isSubmitting}>
          Back
        </Button>
        <Button
          onClick={handleFinish}
          className="flex-1 cursor-pointer"
          size="lg"
          disabled={isSubmitting}
        >
          {isSubmitting ? (
            <span className="flex items-center gap-2">
              <span className="w-4 h-4 rounded-full border-2 border-current/30 border-t-current animate-spin" />
              Saving…
            </span>
          ) : ctaLabel}
        </Button>
      </div>
    </div>
  );
}
